/**
 * Pure decision logic for the microphone level meter.
 *
 * Free of React and of the Web Audio API so it can be unit tested without
 * mocking browser media APIs. Time is always injected, never read from a clock.
 *
 * Background: Chrome does not set MediaStreamTrack.muted when the microphone is
 * muted at the OS level. It hands us a live track full of digital silence, so
 * measuring the signal is the only reliable way to tell a user their mic is dead.
 *
 * We measure the raw waveform (getFloatTimeDomainData), NOT the spectrum. Time
 * domain samples are plain amplitudes in [-1, 1] with an unambiguous scale, they
 * are not affected by the analyser's smoothing, and a muted device produces
 * literal zeros. Frequency-domain dB values are per-FFT-bin and sit on a scale
 * that depends on window and FFT size, which makes any absolute threshold there
 * a guess.
 */

// A muted device emits exact zeros. This sits 3x below one 16-bit converter
// step (3.05e-5), so any device delivering even single-LSB dither counts as
// live.
//
// Deliberately biased against false alarms. Telling someone with a working
// microphone that it is dead, on the screen where they are about to be graded,
// costs more than missing a genuinely dead one: missing it just returns us to
// the behaviour we had before this feature existed. Chrome enables noise
// suppression by default on getUserMedia, which attenuates exactly the idle room
// tone a higher threshold would rely on, so the margin has to come from below.
export const SILENCE_AMPLITUDE = 1e-5;

// Standard peak-meter range in dBFS. 0 dBFS is full scale.
export const METER_MIN_DBFS = -60;
export const METER_MAX_DBFS = 0;
export const METER_SEGMENTS = 12;

// How long silence must persist before we say anything. Before recording we can
// be prompt. During recording we are deliberately slower: the warning is passive,
// and being late is better than nagging someone mid-pitch.
export const PRE_RECORDING_WARNING_MS = 3000;
export const RECORDING_WARNING_MS = 10000;

/**
 * Largest absolute sample in a window of raw waveform data.
 *
 * @param {Float32Array|number[]} samples - getFloatTimeDomainData output, [-1, 1]
 * @returns {number} peak amplitude, 0 for empty or missing input
 */
export function peakAmplitude(samples) {
    if (!samples || !samples.length) {
        return 0;
    }

    let peak = 0;
    for (let i = 0; i < samples.length; i++) {
        const value = samples[i];
        if (isNaN(value)) {
            continue;
        }
        const magnitude = value < 0 ? -value : value;
        if (magnitude > peak) {
            peak = magnitude;
        }
    }
    return peak;
}

/**
 * Convert a peak amplitude to dBFS. Silence maps to -Infinity.
 * @param {number} amplitude
 */
export function amplitudeToDbfs(amplitude) {
    if (typeof amplitude !== 'number' || isNaN(amplitude) || amplitude <= 0) {
        return -Infinity;
    }
    return 20 * Math.log10(amplitude);
}

/**
 * True when no meaningful signal is arriving at all.
 * @param {number} amplitude - peak amplitude for the sample window
 */
export function isSilentAmplitude(amplitude) {
    if (typeof amplitude !== 'number' || isNaN(amplitude)) {
        return true;
    }
    return amplitude < SILENCE_AMPLITUDE;
}

/**
 * Map a dBFS level onto a whole number of lit meter segments.
 *
 * Note this quantizes but does not add hysteresis, so a level hovering exactly
 * on a step boundary will flip between two counts. That is normal meter
 * behaviour and is cheap because the meter owns its own subtree.
 *
 * @param {number} dbfs
 * @returns {number} integer in 0..METER_SEGMENTS
 */
export function levelToSegments(dbfs) {
    if (typeof dbfs !== 'number' || isNaN(dbfs) || dbfs <= METER_MIN_DBFS) {
        return 0;
    }
    if (dbfs >= METER_MAX_DBFS) {
        return METER_SEGMENTS;
    }

    const ratio = (dbfs - METER_MIN_DBFS) / (METER_MAX_DBFS - METER_MIN_DBFS);
    return Math.round(ratio * METER_SEGMENTS);
}

/** Starting tracker. Frozen: it is handed out as a return value. */
export const EMPTY_SILENCE_TRACKER = Object.freeze({ silentSince: null });

/**
 * Advance the silence run-length tracker with a new sample.
 *
 * The returned object identity is stable for as long as a single silent run
 * continues. That is only an allocation optimisation: the run's DURATION keeps
 * growing every tick, so callers must never skip work merely because the
 * identity is unchanged.
 *
 * @param {{silentSince: number|null}} tracker
 * @param {number} amplitude - peak amplitude for this sample window
 * @param {number} now - monotonic ms, injected
 * @returns {{silentSince: number|null}}
 */
export function nextSilenceTracker(tracker, amplitude, now) {
    const current = tracker || EMPTY_SILENCE_TRACKER;

    if (isSilentAmplitude(amplitude)) {
        // Already timing this run: keep the original start time.
        if (current.silentSince !== null) {
            return current;
        }
        return { silentSince: now };
    }

    if (current.silentSince === null) {
        return current;
    }
    return EMPTY_SILENCE_TRACKER;
}

/**
 * How long the current silent run has lasted, 0 when not silent.
 * @param {{silentSince: number|null}} tracker
 * @param {number} now - monotonic ms, injected
 */
export function silenceDurationMs(tracker, now) {
    if (!tracker || tracker.silentSince === null) {
        return 0;
    }
    return Math.max(0, now - tracker.silentSince);
}

/**
 * The single display decision for the meter.
 *
 * Precedence: hidden > muted > warning > normal.
 *
 * `muted` is decided from isMicrophoneActive and NEVER from the measured level.
 * The meter analyses a clone of the mic stream, and MediaStreamTrack.clone()
 * snapshots `enabled` at clone time, so later presses of the app's Mute button
 * do not reach the clone. While in-app muted the meter still reads live audio,
 * and only the flag tells the truth.
 *
 * The warning applies to every meeting type. An earlier draft suppressed it
 * during Q&A Practice on the theory that thinking pauses would false-positive,
 * but a live microphone always emits converter noise above SILENCE_AMPLITUDE,
 * so a silent thinking user never trips it. Suppressing it would have hidden a
 * dead mic from the users it costs the most: in Q&A a dead mic means sitting
 * through a 30s timeout per question and submitting an empty transcript.
 *
 * @param {Object} params
 * @param {boolean} params.available - meter has a working analyser
 * @param {boolean} params.isMicrophoneActive - false when muted via the app's Mute button
 * @param {boolean} params.isRecording
 * @param {number} params.silenceMs
 * @returns {'hidden'|'muted'|'warning'|'normal'}
 */
export function meterDisplayState({ available, isMicrophoneActive, isRecording, silenceMs }) {
    if (!available) {
        return 'hidden';
    }
    if (!isMicrophoneActive) {
        return 'muted';
    }

    const threshold = isRecording ? RECORDING_WARNING_MS : PRE_RECORDING_WARNING_MS;
    if ((silenceMs || 0) >= threshold) {
        return 'warning';
    }

    return 'normal';
}
